import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Users, FileText, Calendar, Globe, Shield, Clock, Phone, GraduationCap, BookOpen, Target, Award, MapPin } from 'lucide-react';

const StudyPermitsGraduatePathways = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section 
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            Study Permits and Graduate Pathways
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            From admissions documents to post-study options, built for real-world compliance.
          </p>
          
          <div className="bg-white rounded-lg shadow-lg p-8 text-left max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-acBlack mb-4">Snapshot</h2>
            <p className="text-gray-600 leading-relaxed">
              We prepare a compliant student visa file and plan your next step after graduation where a route exists. We regularly handle the United States, Canada, United Kingdom, Germany and wider EU, Australia, and the UAE. Response time is 24 to 72 hours. London covers EMEA. New York runs an APAC desk for evening ET overlap.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Who This Is For */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-8 text-center">Who this is for</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              "Students starting language, vocational, undergraduate, or postgraduate study",
              "Parents supporting under-18 applicants", 
              "Graduates who want to move into a permitted post-study work route",
              "Applicants with a prior refusal who need a clearer evidence strategy"
            ].map((item, index) => (
              <div key={index} className="bg-blue-50 rounded-lg p-6 text-center">
                <GraduationCap className="w-8 h-8 text-black mx-auto mb-4" />
                <p className="text-gray-600 font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Outcomes */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-8 text-center">Outcomes you can expect</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: FileText,
                title: "Written Strategy",
                description: "A written strategy that confirms the correct study route, evidence plan, fees, and realistic timeline"
              },
              {
                icon: BookOpen,
                title: "Statement of Purpose",
                description: "A credible Statement of Purpose or GTE or Genuine Student narrative that matches your background and course choice"
              },
              {
                icon: Shield,
                title: "Financial Evidence",
                description: "Financial evidence structured to meet destination rules and acceptable formats"
              },
              {
                icon: CheckCircle,
                title: "Complete Application",
                description: "A quality-checked application ready to submit with appointments booked and interview preparation where required"
              },
              {
                icon: Target,
                title: "Post-Study Plan",
                description: "A post-study plan that maps your options after graduation"
              }
            ].map((outcome, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <outcome.icon className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-semibold text-acBlack mb-3">{outcome.title}</h3>
                <p className="text-gray-600">{outcome.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Eligibility Basics */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-8 text-center">Eligibility basics</h2>
          <div className="space-y-6">
            {[
              "An unconditional offer or the destination's acceptance document such as CAS or LOA",
              "Financial capacity at required levels for tuition, living costs, and any dependent family",
              "A study plan that is consistent with your academic history and future goals",
              "Health, character, and attendance expectations that can be met"
            ].map((requirement, index) => (
              <div key={index} className="flex items-start">
                <CheckCircle className="w-6 h-6 text-blue-600 mr-4 mt-1 flex-shrink-0" strokeWidth={2.5} />
                <p className="text-gray-600 text-lg">{requirement}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Typical Routes */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-8 text-center">Typical routes we cover</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                country: "United Kingdom",
                routes: "Student route with compliance on funds and Academic Progression. Graduate route planning after completion"
              },
              {
                country: "United States", 
                routes: "F-1 with SEVIS registration and proof of non-immigrant intent. OPT and STEM OPT planning for eligible programs"
              },
              {
                country: "Canada",
                routes: "Study Permit with proof of financial capacity and ties. Post-Graduation Work Permit planning for eligible institutions and programs"
              },
              {
                country: "Germany and EU",
                routes: "National student visas and residence permits with blocked account or equivalent funding evidence"
              },
              {
                country: "Australia",
                routes: "Student subclass with Genuine Student criteria and OSHC. Post-study options where available"
              },
              {
                country: "UAE",
                routes: "University sponsorship and family sponsorship where applicable"
              }
            ].map((route, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-xl font-semibold text-acBlack mb-3">{route.country}</h3>
                <p className="text-gray-600">{route.routes}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-gray-600 italic">
              If your destination is not listed, ask during intake. We can coordinate additional countries through partners.
            </p>
          </div>
        </div>
      </motion.section>

      {/* What You Get */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-12 text-center">What you get from us</h2>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Strategy and Planning */}
            <div className="bg-blue-50 rounded-lg p-8">
              <h3 className="text-2xl font-semibold text-acBlack mb-6">Strategy and planning</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Course fit and credibility review against your background</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Country comparison where more than one option is viable</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Financial planning checklist and acceptable evidence formats</span>
                </li>
              </ul>
            </div>

            {/* Document Pack */}
            <div className="bg-gray-50 rounded-lg p-8">
              <h3 className="text-2xl font-semibold text-acBlack mb-6">Document pack and forms</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Statement of Purpose or GTE or Genuine Student templates with edits and alignment to your documents</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Sponsor letters, affidavit language, and bank evidence guidance</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Translations and legalisation instructions where needed</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Application forms completed and dual checked before submission</span>
                </li>
              </ul>
            </div>

            {/* Submission */}
            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-2xl font-semibold text-acBlack mb-6">Submission and follow through</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Online filing or consular filing</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Appointment and biometrics booking</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Interview coaching where required</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Status tracking and proactive updates</span>
                </li>
              </ul>
            </div>

            {/* Post-decision */}
            <div className="bg-blue-50 rounded-lg p-8">
              <h3 className="text-2xl font-semibold text-acBlack mb-6">Post-decision and arrival</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Arrival checklist, address and registration steps where relevant</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Rules on work during study and attendance requirements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-gray-600">Post-study route planning and timelines</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Process Timeline */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-12 text-center">End to end process</h2>
          
          <div className="space-y-8">
            {[
              {
                step: "1",
                title: "Intake and course evidence",
                description: "Offer or CAS or LOA, program details, funding plan, and your academic history."
              },
              {
                step: "2", 
                title: "Credibility and financial build",
                description: "We structure your Statement of Purpose or GTE narrative and align financial evidence to the rules, including acceptable account types and timing."
              },
              {
                step: "3",
                title: "Forms and quality checks", 
                description: "We complete the application, reconcile names and dates across all documents, and run a final review against policy notes."
              },
              {
                step: "4",
                title: "Submission and appointment",
                description: "We submit, pay required fees, schedule biometrics or interview, and prepare you for common questions."
              },
              {
                step: "5",
                title: "Decision and next steps",
                description: "On approval we guide enrolment confirmations, travel timing, and registration steps. If refused, we analyse and advise on refile or review with licensed counsel where required."
              }
            ].map((process, index) => (
              <div key={index} className="flex items-start">
                <div className="bg-blue-500 text-black rounded-full w-10 h-10 flex items-center justify-center font-bold mr-6 flex-shrink-0">
                  {process.step}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-acBlack mb-2">{process.title}</h3>
                  <p className="text-gray-600">{process.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Timelines */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-8 text-center">Timelines and service levels</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-lg font-semibold text-acBlack mb-2">Document pack and forms</h3>
              <p className="text-gray-600">Typically 1 to 2 weeks depending on readiness</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-lg font-semibold text-acBlack mb-2">Government processing</h3>
              <p className="text-gray-600">Varies by country and season. Peak months can extend timelines</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-lg font-semibold text-acBlack mb-2">Communication</h3>
              <p className="text-gray-600">24 to 72 hour response time, with faster responses during booking windows</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      {/* <motion.section 
        className="py-16 px-6 bg-acBlack text-white text-center"
        {...fadeInUp}
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to start your study journey?</h2>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            <Link to="/contact" className="bg-blue-500 hover:bg-blue-600 text-black font-semibold px-8 py-3 rounded-lg transition-colors">
              Check your eligibility
            </Link>
            <Link to="/contact" className="text-white hover:text-blue-300 font-medium underline transition-colors">
              Book a consultation
            </Link>
          </div>
          
          <p className="text-gray-300">
            Replies within 24-72 hours. London and New York coverage.
          </p>
        </div>
      </motion.section> */}
    </div>
  );
};

export default StudyPermitsGraduatePathways;
